/**
 * Auth Handler Service
 * Migrates authentication and routing logic from AngularJS app.module.js _unknownRouteHandler
 */

import {LoaderFunctionArgs, redirect} from 'react-router-dom';
import {IAuthService} from './auth.service';
import {IRedirectionService} from '../RedirectionService';
import {IUserService} from '@techsee/techsee-client-infra/lib/services/UserService';
import {ISsoService as ISsoApiService} from '@techsee/techsee-client-infra/lib/services/SSOService';
import {IUiFlavorService} from '../ts-ui-flavor/ts-ui-flavor.service';
import {IBrowserUtilsExtended} from '../browser-utils-extended/browser-utils-extended.service';
import {getAppTracer} from '../../../app.tracer';
import {PromiseUtilsService} from '@techsee/techsee-client-services/lib/services/PromiseUtilsService';
import {UrlUtilsService} from '@techsee/techsee-client-services/lib/services/UrlUtilsService';
import {RouteName, RouteNameType} from '../../router/route-names';
import {sendSentryLog} from '../../../error-reporting';
// @ts-ignore
import {getSubDomainFromUrl} from '@techsee/techsee-common/lib/utils';

/**
 * Interface for authentication handler results
 */
export interface AuthHandlerResult {
    authorized: boolean;
    state?: RouteNameType;
    params?: Record<string, any>;
}

// Import from same location as app.module.js
const TsUtils = require('@techsee/techsee-common/lib/utils');
const {createUniqueUserName} = TsUtils;

declare const BASE_PATH: string;
declare const ENV: any;

const trace = getAppTracer('auth-handler.service');
const INVITE_PATH = '/invite';

function normalizePath(pathname: string): string {
    const normalizedPath = pathname.replace(/\/+$/, '');
    return normalizedPath || '/';
}

export function buildLoginRedirectParamsFromUrl(url: URL): Record<string, string> {
    const preservedParams = Object.fromEntries(url.searchParams.entries());
    const returnQuery = `${url.pathname}${url.search}`;

    if (normalizePath(url.pathname) === INVITE_PATH && Object.keys(preservedParams).length > 0) {
        return preservedParams;
    }

    return {...preservedParams, returnQuery};
}

export interface QueryParams {
    r?: string;
    p?: string;
    c?: string;
    is?: string;
    roomId?: string;
    g?: string;
    roomCode?: string;
    audio?: string;
    observe?: string;
    emb?: string;
    mini?: string;
    authId?: string;
    ott?: string;
    tablet?: string;
    sw?: string;
    prg?: string;
    e?: string;
    startDate?: string;
    endDate?: string;
    scanId?: string;
    'sophie-ctx'?: string;
    logout?: string;
    user?: string;
    communityId?: string;
    state?: string;
    code?: string;
    error?: string;
}

/**
 * Extract query parameters from URL
 */
function extractQueryParams(request: Request): QueryParams {
    const url = new URL(request.url);
    let params: QueryParams = {};

    const keys: (keyof QueryParams)[] = [
        'r',
        'p',
        'c',
        'is',
        'roomId',
        'g',
        'roomCode',
        'audio',
        'observe',
        'emb',
        'mini',
        'authId',
        'ott',
        'tablet',
        'sw',
        'prg',
        'e',
        'startDate',
        'endDate',
        'scanId',
        'sophie-ctx',
        'logout',
        'user',
        'communityId',
        'state',
        'code',
        'error'
    ];

    const decodeParam = (value: string): string => {
        try {
            return decodeURIComponent(value);
        } catch {
            return value;
        }
    };

    keys.forEach((key) => {
        const value = url.searchParams.get(key);
        if (value) {
            params[key] = decodeParam(value);
        }
    });

    if (params.state) {
        try {
            const state = JSON.parse(params.state);

            params = {...params, ...state};
        } catch {
            // ignore malformed state payload
        }
    }

    return params;
}

type CurrentUserParams = {
    alias?: string;
    returnURL?: string;
    userAgent?: string;
    customerId?: string;
    customerNumber?: string;
    customerCountryCode?: string;
    initialState?: string;
    roomId?: string;
    guid?: string;
    roomCode?: string;
    audio?: string;
    subDomain?: string;
    sw?: string;
    prg?: string;
    observe?: string;
    communityId?: string;
    startDate?: string;
    endDate?: string;
};

export class AuthHandlerService {
    public isInSsoAuthFlow: boolean = false;

    constructor(
        private authService: IAuthService,
        private redirectionService: IRedirectionService,
        private userService: IUserService,
        private ssoApiService: ISsoApiService,
        private browserUtilsService: IBrowserUtilsExtended,
        private uiFlavorService: IUiFlavorService
    ) {}

    async handleOAuthCallback(code?: string, error?: string): Promise<AuthHandlerResult | null> {
        if (!code && !error) {
            return null;
        }

        try {
            const scope = UrlUtilsService.extractQueryParamValueFromUrl(window.location.search, 'scope');
            const state = UrlUtilsService.extractQueryParamValueFromUrl(window.location.search, 'state');
            const errorDescription = UrlUtilsService.extractQueryParamValueFromUrl(
                window.location.search,
                'error_subtype'
            );

            const payload: any = {state};
            if (scope) payload.scope = scope;
            if (code) payload.code = code;
            if (error) payload.error = error;
            if (errorDescription) payload.errorDescription = errorDescription;

            const res: any = await this.ssoApiService.oAuthLoginCallback(payload);
            const redirectState = res?.data || res?.redirectUrl;
            if (!redirectState) {
                throw new Error('No redirect URL from OAuth callback');
            }

            this.redirectionService.goToUrl(redirectState);

            return {authorized: false};
        } catch (err) {
            const traceMessage = `Failed to redirect from OAuth callback: ${err}`;
            await PromiseUtilsService.startPromiseWithTimeout(() => trace.error(traceMessage, err), 3000).catch(
                (traceErr) => trace.error(traceMessage + ' >> trace failed: ' + traceErr)
            );

            sendSentryLog(err);
            return {authorized: false, state: RouteName.LOGIN};
        }
    }

    async handleUnknownRoute(
        params: QueryParams,
        alreadyLoggedOut: boolean = false,
        request?: Request
    ): Promise<AuthHandlerResult | {user?: any}> {
        // Guest mode: skip SSO - guest uses OTT redemption separately
        if (params.observe === 'guest') {
            return {authorized: false, state: RouteName.LOGIN, params: this.extractLoginParams(params)};
        }

        const {getAuthToken, loginWithOneTimeToken, logoutWithoutSSO, clearAuthToken} = this.authService;

        // Check for OAuth callback
        const oAuthResult = await this.handleOAuthCallback(params.code, params.error);
        if (oAuthResult) {
            return oAuthResult;
        }

        this.isInSsoAuthFlow = true;
        try {
            const response = await this.ssoCurrentUser(
                {getParamValue: (key: string) => params[key as keyof QueryParams]} as any,
                this.userService.getCurrentUser,
                getAuthToken,
                loginWithOneTimeToken,
                params.ott || ''
            );

            if (response?.redirect) {
                // Same recovery order as the pre-React handler (v2.202.x app.module.js): a stored
                // token whose backend session expired yields a redirect response with no userName.
                // Clear the stale token and retry so a pending OTT can be redeemed, instead of
                // bouncing the user to the IDP login page.
                if (params.user && !response.userName && !alreadyLoggedOut && !response.wasRedeemed && getAuthToken()) {
                    trace.info('SSO redirect received while holding a stale token, clearing token and retrying');
                    clearAuthToken();

                    return this.handleUnknownRoute(params, true);
                }

                const ssoUrl = response.redirect;
                throw redirect(ssoUrl);
            }

            return this.routeAuthenticatedUser(response, params, alreadyLoggedOut);
        } catch (err) {
            if (err instanceof Response) throw err;

            // Check if this is a recoverable HTTP error and we have an auth token
            const status = (err as any)?.response?.status;
            const isRecoverableError = status === 400 || status === 401 || status === 403;
            const hasAuthToken = !!getAuthToken();

            if (isRecoverableError && hasAuthToken) {
                trace.info('SSO auth failed with status ' + status + ', attempting token-based auth fallback');

                try {
                    const response = await this.userService.getCurrentUser();

                    if (response && (response as any).userName) {
                        return this.routeAuthenticatedUser(response, params, alreadyLoggedOut);
                    }
                } catch (tokenErr) {
                    trace.error('Token-based getCurrentUser also failed', tokenErr);
                    sendSentryLog(tokenErr);
                }
            }

            const traceMessage = `App.module failed - ssoCurrentUser: ${JSON.stringify({
                isMinidashboard: params.mini,
                isEmbeddedDashboard: params.emb
            })}`;

            await PromiseUtilsService.startPromiseWithTimeout(() => trace.error(traceMessage, err), 3000);

            sendSentryLog(err);
            if (params.emb) {
                return {authorized: false, state: RouteName.EMB_DASHBOARD_ERROR};
            }

            return {authorized: false, state: RouteName.LOGIN, params: this.extractLoginParams(params)};
        } finally {
            this.isInSsoAuthFlow = false;
        }
    }

    private async routeAuthenticatedUser(
        response: any,
        params: QueryParams,
        alreadyLoggedOut: boolean
    ): Promise<AuthHandlerResult | {user?: any}> {
        const {logoutWithoutSSO, clearAuthToken} = this.authService;

        // Handle embedded dashboard failure
        if (response && response.embeddedDashboardFailure) {
            trace.error(
                'App.module failed - ssoCurrentUser result: ' + JSON.stringify({isEmbeddedDashboard: true}),
                response.embeddedDashboardFailureError
            );
            sendSentryLog(response.embeddedDashboardFailureError);
            return {authorized: false, state: RouteName.EMB_DASHBOARD_ERROR};
        }

        // Handle guest mode
        if (params.observe === 'guest') {
            return {authorized: false, state: RouteName.LOGIN, params: this.extractLoginParams(params)};
        }

        const {userName, accountId, _id: userId, wasRedeemed: ottRedeemed} = response;
        const logoutUser = params.logout === 'true';
        const sandboxUserName = createUniqueUserName({shouldCreate: true}, params.user, accountId);
        const isExpectedUserLoggedIn = params.user === userName || sandboxUserName === userName;

        // Handle logout scenarios
        if (
            !alreadyLoggedOut &&
            ((params.user && userName && !isExpectedUserLoggedIn) || (logoutUser && !ottRedeemed))
        ) {
            await logoutWithoutSSO(accountId, userId);
            return this.handleUnknownRoute(params, true);
        }

        // Handle expired token
        if (params.user && !userName && !alreadyLoggedOut && !ottRedeemed) {
            clearAuthToken();
            return this.handleUnknownRoute(params, true);
        }

        // Handle redirects based on response
        if (response.redirect) {
            return {authorized: false, state: response.redirect};
        }

        // Route based on 'is' parameter
        const isParam = params.is?.toLowerCase();
        if (isParam) {
            if (isParam.includes('vjhistory')) {
                return {
                    authorized: false,
                    state: RouteName.VJ_HISTORY,
                    params: {
                        authId: params.authId,
                        r: params.r,
                        startDate: params.startDate,
                        endDate: params.endDate,
                        is: params.is,
                        roomId: params.roomId
                    }
                };
            }

            if (isParam.includes('historyconsole')) {
                return {
                    authorized: false,
                    state: RouteName.HISTORY_CONSOLE,
                    params: this.browserUtilsService.cleanParams({
                        authId: params.authId,
                        r: params.r,
                        p: params.p,
                        e: params.e,
                        roomId: params.roomId,
                        startDate: params.startDate,
                        endDate: params.endDate,
                        scanId: params.scanId,
                        is: params.is
                    })
                };
            }

            if (isParam.includes('history') && (params.r || params.p)) {
                return {
                    authorized: false,
                    state: RouteName.HISTORY,
                    params: {
                        authId: params.authId,
                        customerId: params.r,
                        customerNumber: params.p,
                        countryCode: params.c
                    }
                };
            }

            if (isParam.includes('historysearchbytags')) {
                return {
                    authorized: false,
                    state: RouteName.HISTORY,
                    params: {
                        authId: params.authId,
                        isSearchByTags: true
                    }
                };
            }

            if (isParam.includes('admin')) {
                await this.redirectionService.goToAdmin();

                // goToAdmin() does a hard redirect; this return is a dead-code fallback
                return {authorized: false, state: RouteName.LOGIN};
            }
        }

        // Default to invite with all params
        return {authorized: false, state: RouteName.INVITE, params: this.extractInviteParams(params)};
    }

    private tryLoginWithOTT(
        tsUrlUtils: any,
        findCurrentUser: any,
        getAuthToken: any,
        loginWithOneTimeToken: any,
        ott: string,
        currentUserParams: CurrentUserParams
    ): Promise<any> {
        let wasRedeemed = false;

        return loginWithOneTimeToken(ott)
            .then((res: {isRedeemed: boolean}) => {
                wasRedeemed = res.isRedeemed;

                if (wasRedeemed) {
                    this.browserUtilsService.setParamValue('ott', null);

                    if (getAuthToken()) {
                        return {redirect: false, wasRedeemed: true};
                    }

                    if (tsUrlUtils.getParamValue('emb')) {
                        return {
                            embeddedDashboardFailure: true,
                            embeddedDashboardFailureError: 'Auth with OTT was not successful'
                        };
                    }
                }

                return findCurrentUser(currentUserParams).then((res: any) => {
                    if (!res) {
                        throw new Error('User Not Found');
                    } else return res;
                });
            })
            .catch((err: any) => {
                // Might have been using stale token from local storage
                if (!wasRedeemed && getAuthToken()) {
                    console.warn('An invalid pre-existing token found, trying again with OTT', err);

                    this.authService.clearAuthToken();

                    return this.tryLoginWithOTT(
                        tsUrlUtils,
                        findCurrentUser,
                        getAuthToken,
                        loginWithOneTimeToken,
                        ott,
                        currentUserParams
                    );
                }

                if (tsUrlUtils.getParamValue('emb')) {
                    sendSentryLog(err);

                    // OTT failure with embedded dashboard should not be blocked
                    return {
                        embeddedDashboardFailure: true,
                        embeddedDashboardFailureError: err
                    };
                }

                throw err;
            });
    }

    public ssoCurrentUser(
        tsUrlUtils: any,
        findCurrentUser: any,
        getAuthToken: any,
        loginWithOneTimeToken: any,
        ott: string
    ) {
        const secondLogin = this.browserUtilsService.getFromSessionStorage('ssoObserver');
        const subDomain = getSubDomainFromUrl(window.location.href);
        const alias = location.pathname.split('/')[1];
        const origin = window.location.origin;
        const observe = tsUrlUtils.getParamValue('observe');
        const communityId = tsUrlUtils.getParamValue('communityId');

        // @ts-ignore
        const returnUrl = origin + (ENV.dev ? '/#' : '') + BASE_PATH + 'login-callback';

        const currentUserParams: CurrentUserParams = {
            alias: secondLogin ? undefined : alias,
            returnURL: returnUrl,
            userAgent: window.navigator && window.navigator.userAgent,
            customerId: tsUrlUtils.getParamValue('r'),
            customerNumber: tsUrlUtils.getParamValue('p'),
            customerCountryCode: tsUrlUtils.getParamValue('c'),
            initialState: tsUrlUtils.getParamValue('is'),
            roomId: tsUrlUtils.getParamValue('roomId'),
            guid: tsUrlUtils.getParamValue('g'),
            roomCode: tsUrlUtils.getParamValue('roomCode'),
            audio: tsUrlUtils.getParamValue('audio'),
            subDomain: subDomain,
            sw: tsUrlUtils.getParamValue('sw'),
            prg: tsUrlUtils.getParamValue('prg'),
            startDate: tsUrlUtils.getParamValue('startDate'),
            endDate: tsUrlUtils.getParamValue('endDate'),
            observe
        };

        if (communityId) {
            currentUserParams.communityId = communityId;
        }

        if (!secondLogin) {
            this.uiFlavorService.setLastUsedAlias(alias);
        }

        if (observe && !secondLogin) {
            this.browserUtilsService.saveToSessionStorage('ssoObserver', true);
        }

        if (ott && observe !== 'guest') {
            return this.tryLoginWithOTT(
                tsUrlUtils,
                findCurrentUser,
                getAuthToken,
                loginWithOneTimeToken,
                ott,
                currentUserParams
            );
        }

        return PromiseUtilsService.startPromiseWithTimeout(
            () => trace.info('Before login check', currentUserParams),
            3000
        ).then(() =>
            findCurrentUser(currentUserParams).then((res: any) => {
                if (!res) {
                    throw new Error('User Not Found');
                } else return res;
            })
        );
    }

    private extractLoginParams(params: QueryParams): Record<string, any> {
        return {
            r: params.r,
            p: params.p,
            c: params.c,
            is: params.is,
            roomId: params.roomId,
            g: params.g,
            roomCode: params.roomCode,
            audio: params.audio,
            observe: params.observe,
            emb: params.emb,
            mini: params.mini,
            ott: params.ott,
            tablet: params.tablet,
            sw: params.sw,
            prg: params.prg,
            communityId: params.communityId,
            startDate: params.startDate,
            endDate: params.endDate,
            'sophie-ctx': params['sophie-ctx']
        };
    }

    private extractInviteParams(params: QueryParams): Record<string, any> {
        return {
            r: params.r,
            p: params.p,
            c: params.c,
            is: params.is,
            roomId: params.roomId,
            g: params.g,
            roomCode: params.roomCode,
            audio: params.audio,
            observe: params.observe,
            emb: params.emb,
            mini: params.mini,
            authId: params.authId,
            ott: params.ott,
            tablet: params.tablet,
            sw: params.sw,
            prg: params.prg,
            communityId: params.communityId,
            'sophie-ctx': params['sophie-ctx']
        };
    }

    /**
     * Protected route authentication check
     * Returns redirect Response if not authenticated, or authorized result if authenticated
     */
    async handleProtectedRoute(request: Request, ignoreReturnUrl?: boolean): Promise<AuthHandlerResult> {
        try {
            const token = this.authService.getAuthToken();

            if (!token) {
                const url = new URL(request.url);
                const params = ignoreReturnUrl ? undefined : buildLoginRedirectParamsFromUrl(url);
                return {authorized: false, state: RouteName.LOGIN, params};
            }

            // Authenticated, allow access
            return {authorized: true};
        } catch (error) {
            trace.error('handleProtectedRoute error:', error);
            sendSentryLog(error);
            return {authorized: false, state: RouteName.LOGIN};
        }
    }

    /**
     * Guest route authentication check
     * Allows access only when observe=guest is present
     * Preserves all query params when redirecting
     */
    async handleGuestRoute(request: Request): Promise<AuthHandlerResult> {
        try {
            const url = new URL(request.url);
            const observe = url.searchParams.get('observe');

            if (observe === 'guest') {
                return {authorized: true};
            }

            // Not a guest, redirect to login WITH ALL PARAMS PRESERVED
            const preservedParams = Object.fromEntries(url.searchParams.entries());
            return {authorized: false, state: RouteName.LOGIN, params: preservedParams};
        } catch (error) {
            trace.error('handleGuestRoute error:', error);
            sendSentryLog(error);
            return {authorized: false, state: RouteName.LOGIN};
        }
    }
}

/**
 * React Router Loaders
 * These functions are used directly by React Router
 * Thin wrappers that delegate to service methods
 */

/**
 * Authentication guard loader
 * Handles unknown routes and authentication logic
 */
export async function unknownRouteLoader(args: LoaderFunctionArgs): Promise<Record<string, any>> {
    const {getRootStore} = await import('../../app.bootstrap');
    const rootStore = getRootStore();

    // Await root services initialization to ensure authService and other dependencies are ready
    await rootStore.initRootServicesCompleted;

    const params = extractQueryParams(args.request);
    const result = await rootStore.authHandlerService.handleUnknownRoute(params, false, args.request);

    if (result && typeof result === 'object' && 'state' in result && typeof result.state === 'string') {
        await rootStore.tsReactStateHelper.navigateTo(
            result.state as RouteNameType,
            (result as AuthHandlerResult).params
        );
    }

    return result;
}

/**
 * Protected route guard
 * Ensures user is authenticated before accessing route
 */
export async function protectedRouteLoader(
    args: LoaderFunctionArgs,
    options?: {ignoreReturnUrl?: boolean}
): Promise<AuthHandlerResult> {
    const {getRootStore} = await import('../../app.bootstrap');
    const rootStore = getRootStore();

    return await rootStore.authHandlerService.handleProtectedRoute(args.request, options?.ignoreReturnUrl);
}

/**
 * Guest route guard
 * Allows access only when observe=guest is present
 * Preserves all query params when redirecting
 */
export async function guestRouteLoader(args: LoaderFunctionArgs): Promise<AuthHandlerResult> {
    const {getRootStore} = await import('../../app.bootstrap');
    const rootStore = getRootStore();

    return await rootStore.authHandlerService.handleGuestRoute(args.request);
}
